import type { ReactElement } from 'react'
import React from 'react'
import moment from 'moment'
import {
  Box,
  Flex,
  Text,
  Avatar,
  Tooltip,
  Text as ChakraText,
  AvatarBadge,
  AspectRatio,
  HStack,
  Icon,
} from '@chakra-ui/react'

import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { Base64 } from 'js-base64'
import { FaFile } from 'react-icons/fa'
import { NotificationsOperationsEnum } from '../../../../utils/operations'
import type {
  Notification,
  FileDetailsResponse,
  Asset,
} from '../../../interfaces'
import LazyEditor from '../../TextEditor/LazyEditor'
import FilePreview from '../FilePreview'
import { mediaLibraryThumbnailTransformation } from '../../../../utils/mediaLibrary'
import { CollectionIcon } from '../../../../graphics/media-library/CollectionIcon'
import { FolderIcon } from '../../../../graphics/media-library/FolderIcon'

const AvatarWrapper = styled.div`
  .hover-box {
    display: flex;
    width: 42px;
    height: 42px;
    position: relative;
    align-items: center;
    justify-content: center;
  }

  .avatar {
    display: block;
  }

  .avatar-badge {
    display: block;
  }

  .actionBox {
    display: flex;
    border: none;

    .innerDot {
      display: flex;
      border: none;
    }
  }

  .hover-box:hover .avatar,
  .hover-box:hover .avatar-badge {
    display: none;
  }

  .hover-box:hover .actionBox,
  .hover-box:hover .innerDot {
    display: flex;
    border: 1px solid #e2e8f0;
    cursor: pointer;
  }

  .hover-box:not(:hover) .actionBox .innerDot {
    display: none;
  }
`

interface ReadMapRecordValue {
  new: boolean
  original: boolean
}

const getRedirectPath = (asset: Notification['asset']): string => {
  if (asset.type === 'file') {
    return `/media-library/detail/${
      (asset as FileDetailsResponse).fileId
        ? (asset as FileDetailsResponse).fileId
        : (asset as Asset).id
    }`
  } else if (asset.type === 'file-version') {
    return `/media-library/detail/${(asset as FileDetailsResponse).fileId}/${
      (asset as FileDetailsResponse).versionInfo.id
    }`
  } else if (asset.type === 'folder' && (asset as Asset).path) {
    return `/media-library/${Base64.encode((asset as Asset).path ?? '')}`
  } else if (asset.type === 'media-collection') {
    return `/media-collections/${(asset as Asset).id}`
  }
  return ''
}

function NotificationLabel({
  notification,
  maxW,
}: {
  notification: Notification
  maxW?: string
}): JSX.Element {
  const { operation, operationDetails, operator, asset } = notification
  switch (operation) {
    case NotificationsOperationsEnum.ADD_COMMENT: {
      if (operationDetails.mentioned) {
        return (
          <ChakraText maxW={maxW}>
            <Tooltip label={operator.email}>
              <ChakraText as="span" fontWeight="600">
                {operator.name}
              </ChakraText>
            </Tooltip>{' '}
            mentioned you on{' '}
            <Tooltip label={(asset as Asset).path}>
              <ChakraText
                as={Link}
                _hover={{ textColor: '#2c93fa' }}
                fontWeight="600"
                to={getRedirectPath(asset)}
                textOverflow="ellipsis"
              >
                {asset.name}
              </ChakraText>
            </Tooltip>
          </ChakraText>
        )
      }
      return (
        <ChakraText maxW={maxW}>
          <Tooltip label={operator.email}>
            <ChakraText as="span" fontWeight="600">
              {operator.name}
            </ChakraText>
          </Tooltip>{' '}
          commented on{' '}
          <Tooltip label={(asset as Asset).path}>
            <ChakraText
              as={Link}
              fontWeight="600"
              to={getRedirectPath(asset)}
              _hover={{ textColor: '#2c93fa' }}
              textOverflow="ellipsis"
            >
              {asset.name}
            </ChakraText>
          </Tooltip>
        </ChakraText>
      )
    }
    case NotificationsOperationsEnum.SHARE_ASSET: {
      return (
        <ChakraText maxW={maxW}>
          <Tooltip label={operator.email}>
            <ChakraText as="span" fontWeight="600">
              {operator.name}
            </ChakraText>
          </Tooltip>{' '}
          shared{' '}
          <Tooltip label={(asset as Asset).path}>
            <ChakraText
              as={Link}
              fontWeight="600"
              to={getRedirectPath(asset)}
              _hover={{ textColor: '#2c93fa' }}
            >
              {asset.name}
            </ChakraText>
          </Tooltip>
        </ChakraText>
      )
    }
    default:
      return <></>
  }
}

function NotificationContent({
  notification,
}: {
  notification: Notification
}): ReactElement {
  const { operation, operationDetails, asset } = notification
  switch (operation) {
    case NotificationsOperationsEnum.ADD_COMMENT: {
      if (operationDetails.commentBody) {
        return (
          <Flex
            as={Link}
            to={`/media-library/detail/${
              (asset as FileDetailsResponse).fileId
                ? (asset as FileDetailsResponse).fileId
                : (asset as Asset).id
            }?focusedThreadId=${operationDetails?.threadId}`}
            padding="4px 4px 4px 17.7px"
            borderRadius="4px"
            border="solid 1px #e6e7ea"
            backgroundColor="white"
          >
            <LazyEditor
              isNewThread={true}
              editable={false}
              initialEditorState={operationDetails.commentBody}
            />
          </Flex>
        )
      }
      return <></>
    }
    default:
      return <></>
  }
}

function NotificationElement({
  notification,
  readStatus,
  changeReadStatus,
}: {
  notification: Notification
  readStatus: boolean
  changeReadStatus(readStatus: boolean, notificationId: string): void
}): ReactElement {
  const { t } = useTranslation()
  return (
    <Flex
      width="100%"
      minW="100%"
      padding="1rem"
      bg={readStatus ? 'white' : '#f7fafc'}
    >
      <AvatarWrapper>
        <Box className="hover-box">
          <Avatar
            className="avatar"
            key={notification.notificationId}
            name={notification.operator.name}
          >
            <AvatarBadge
              className="avatar-badge"
              boxSize="1em"
              bg="blue.500"
              visibility={!readStatus ? 'visible' : 'hidden'}
              placement="top-end"
            />
          </Avatar>
          <Tooltip
            hasArrow
            closeOnClick={false}
            label={
              readStatus
                ? t('mark_as_unread', 'Mark as unread')
                : t('mark_as_read', 'Mark as read')
            }
          >
            <Box
              className="actionBox"
              h="42px"
              w="42px"
              borderRadius="50%"
              border="1px solid #E2E8F0"
              alignItems="center"
              justifyContent="center"
              onClick={() => {
                changeReadStatus(!readStatus, notification.notificationId)
              }}
            >
              <Box
                className="innerDot"
                h="16px"
                w="16px"
                borderRadius="50%"
                border="0.2rem solid #E2E8F0"
                bg="blue.500"
                visibility={!readStatus ? 'visible' : 'hidden'}
              />
            </Box>
          </Tooltip>
        </Box>
      </AvatarWrapper>

      <Flex margin="0rem 1rem" direction="column" w="335px">
        <Flex marginBottom="0.5rem" direction="column" w="100%">
          <HStack justifyContent="space-between" w="100%">
            <NotificationLabel maxW="70%" notification={notification} />
            {['file', 'file-version'].includes(notification.asset.type) &&
              (notification.asset as FileDetailsResponse).thumbnail && (
                <AspectRatio minW="40px" minH="40px" w="40px" h="40px">
                  <FilePreview
                    file={notification.asset as FileDetailsResponse}
                    folderPath={
                      (notification.asset as FileDetailsResponse).filePath
                    }
                    transformation={mediaLibraryThumbnailTransformation}
                    disablePadding={true}
                  />
                </AspectRatio>
              )}
            {['file', 'file-version'].includes(notification.asset.type) &&
              !(notification.asset as FileDetailsResponse).thumbnail && (
                <Icon as={FaFile} boxSize="40px" color="rgb(160, 174, 192)" />
              )}
            {notification.asset.type === 'folder' && (
              <FolderIcon w="40px" h="40px" />
            )}
            {notification.asset.type === 'media-collection' && (
              <CollectionIcon w="40px" h="40px" />
            )}
          </HStack>
          <Text color="#4A5568" fontSize="12px">
            {moment(notification.createdAt).fromNow()}
          </Text>
        </Flex>
        <NotificationContent notification={notification} />
      </Flex>
    </Flex>
  )
}

export default React.memo(NotificationElement)
